import { useState, useEffect } from 'react';
import { useAuth, lookupEmailByDisplayName } from '@/lib/auth';
import { getClubLogoUrl } from '@/lib/supabase';

const DEFAULT_LOGO_URL = 'https://images.pexels.com/photos/209861/pexels-photo-209861.jpeg?auto=compress&cs=tinysrgb&w=200&h=200&dpr=1';

export default function Login() {
  const { signIn, loading } = useAuth();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const [logoLoading, setLogoLoading] = useState(true);

  useEffect(() => {
    getClubLogoUrl()
      .then(url => setLogoUrl(url))
      .catch(() => setLogoUrl(null))
      .finally(() => setLogoLoading(false));
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);

    try {
      const email = await lookupEmailByDisplayName(username.trim());
      const { error: signInError } = await signIn(email, password);
      if (signInError) {
        setError('Invalid username or password');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Invalid username or password');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-surface-raised border-t-team-a rounded-full animate-spin mx-auto mb-4" />
          <p className="text-text-secondary">Loading...</p>
        </div>
      </div>
    );
  }

  const displayLogo = logoUrl || DEFAULT_LOGO_URL;

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="w-full max-w-sm">
        <div className="text-center mb-8">
          <div className="mb-6">
            {logoLoading ? (
              <div className="w-20 h-20 mx-auto rounded-full border-4 border-surface-raised border-t-team-a animate-spin" />
            ) : (
              <img
                src={displayLogo}
                alt="Starwood Sports Club"
                className="w-20 h-20 mx-auto rounded-full object-cover shadow-lg ring-2 ring-surface-raised"
              />
            )}
          </div>
          <h1 className="text-2xl font-bold text-text-primary mb-1">Starwood Sports Club</h1>
          <p className="text-text-muted text-sm">Throwball Tournament Scoring System</p>
        </div>

        <form onSubmit={handleSubmit} className="card space-y-4">
          <div>
            <label className="block text-sm text-text-secondary mb-2">Username</label>
            <input
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              className="input-field"
              placeholder="Enter your username"
              required
              autoComplete="username"
              autoCapitalize="off"
              autoCorrect="off"
            />
          </div>

          <div>
            <label className="block text-sm text-text-secondary mb-2">Password</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="input-field"
              placeholder="Enter your password"
              required
              autoComplete="current-password"
            />
          </div>

          {error && (
            <div className="bg-error/20 border border-error rounded-lg p-3">
              <p className="text-error text-sm">{error}</p>
            </div>
          )}

          <button
            type="submit"
            disabled={submitting}
            className="btn-primary bg-team-a text-white"
          >
            {submitting ? (
              <span className="flex items-center justify-center gap-2">
                <span className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                SIGNING IN...
              </span>
            ) : (
              'SIGN IN'
            )}
          </button>
        </form>
      </div>
    </div>
  );
}
